import { ChangeDetectorRef, Component, OnInit, signal } from '@angular/core';
import { QRScanner, QRScannerStatus } from '@ionic-native/qr-scanner/ngx';
import { Vibration } from '@awesome-cordova-plugins/vibration/ngx/';
import { NavController, Platform, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, finalize, take, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { Broadcaster } from 'src/app/services/events/broadcaster.class';
import { NetworkService } from 'src/app/services/network/network.service';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { ITenantLite } from 'src/app/services/tenant/models/tenant-lite.model';
import { ITenant } from 'src/app/services/tenant/models/tenant.model';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { IUser } from 'src/app/services/user/models/user.model';
import { UserService } from 'src/app/services/user/user.service';
import { EventService } from 'src/app/services/events/event.service';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { ErrorService } from '@app/services/error/error.service';
import { DelegatedConsentsService } from '@app/services/delegated-consents/delegated-consents.service';
import { IDelegatedConsent } from '@app/services/delegated-consents/models/delegated-consent.model';

@Component({
    selector: 'app-go',
    templateUrl: './go.page.html',
    styleUrls: ['./go.page.scss'],
    standalone: false
})
export class GoPage implements OnInit {

  private unSubscribe = new Subject<void>();
  /**
   * Teardown for the consents fetch, separate from `unSubscribe`.
   *
   * It was originally separate out of necessity: `translateMessageWithParameter()` used to call
   * `next()` **and `complete()`** on `unSubscribe` from inside a translation callback, so the first
   * toast of the page's life cancelled an in-flight consents fetch and left every other
   * `takeUntil(this.unSubscribe)` on the page unable to cancel anything. That is fixed at source
   * now, so this is no longer required — it is kept as a narrow, independent teardown for one
   * feature. Note the spec case that simulates a toast completing `unSubscribe` describes what the
   * code used to do, not what it does: it survives as a guard against the pattern coming back.
   * Collapse this into `unSubscribe` if you would rather have one subject.
   *
   * Only ever `next()`ed, never `complete()`d: this page is re-entered rather than recreated, and
   * `takeUntil` on a completed notifier can never fire again.
   */
  private consentsUnSubscribe = new Subject<void>();

  /**
   * False once the user has left, so late work knows not to act.
   *
   * `ionViewDidEnter()`'s tenant/user chain is deliberately not cancelled on leave — cancelling it
   * strands or steals the app-wide loading spinner, and skips the `checkParams()` that registers a
   * card from a `?registrationCode=…` deep link. So it can still resolve after the page is gone,
   * and `setListeners()` checks this rather than subscribing listeners nothing will tear down.
   */
  private pageIsActive = false;

  private reloadPageEventSubscription: Subscription = null;

  public  user = this.userService.userSignal;
  public tenant = this.tenantService.tenantSignal;

  public overlay: boolean = false;
  public idCodeMarkup: boolean = false;
  public userManageIdCode: boolean = false;
  public userManagePinCode: boolean = false;
  public userManageCard: boolean = false;
  public showRegisterCardDeniedDialog: boolean = false;
  public gettingIdCode: boolean = true;
  public gettingPinCode: boolean = true;
  public idCode: string = null;
  public idCodeResource: any = null;
  public pinCodeResource: any = null;
  public isIOS: boolean = this.platformService.isIOS();
  public isCordova: boolean = this.platformService.isCordova();


  public nav: any;
  public newPinCode: string = '';
  public enterPinCode: boolean = false;
  public pinCodeConflict: boolean = false;
  public invalidPinCode: boolean = false;
  public incompletePinCode: boolean = true;

  public cardCode: string = '';
  public enterCardCode: boolean = false;
  public invalidCardCode: boolean = false;
  public invalidQRCode: boolean = false;
  public incompleteCardCode: boolean = true;
  public cards: Array<any> = [];

  public getTenantAndUserSubscription = new Subject<void>()
  public showTransparentBody: boolean = false;
  public isOnline = this.networkService.hasInternetAccess;

  public delegatedConsents = signal<IDelegatedConsent[]>([]);

  private resumeSubscription: Subscription = null;
  private logger: Logger;

  constructor(
    private authService: AuthService,
    private broadcaster: Broadcaster,
    private delegatedConsentsService: DelegatedConsentsService,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private eventService: EventService,
    private loggingService: LoggingService,
    private navController: NavController,
    private networkService: NetworkService,
    private pageTitleService: PageTitleService,
    private platform: Platform,
    private platformService: PlatformService,
    private registerCardScannerQR: QRScanner,
    private ref: ChangeDetectorRef,
    private storageService: StorageService,
    private tenantService: TenantService,
    private toastController: ToastController,
    private translateService: TranslateService,
    private userService: UserService,
    private vibration: Vibration,
  ) {
    this.logger = loggingService.getLogger("[GoPage]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  ngOnInit() {
    this.pageTitleService.setTitle('CardsCodesAndConsents');
  }

  ionViewWillLeave(): void {
    this.pageIsActive = false;
    this.invalidQRCode = false;
    this.toggleQRScanningState(false);
    // Optional: setListeners() only runs on the success path of ionViewDidEnter's tenant/user
    // chain, so on an error path this field is still null — and an unguarded call here would
    // throw before the teardown below it ever ran.
    this.reloadPageEventSubscription?.unsubscribe();
    this.resumeSubscription?.unsubscribe();
    this.resumeSubscription = null;
    // next() only, on both — never complete(). This page is re-entered rather than recreated, and
    // takeUntil on a completed notifier can never fire again, which is the bug this used to have.
    //
    // Be clear about what this achieves today: very little. unSubscribe was never torn down here
    // before, but of its remaining chains the toast translation has already emitted synchronously,
    // and the QR scan was destroyed by toggleQRScanningState(false) above. The initPage()
    // ErrorService call is deliberately exempt (see there). This exists so the next
    // takeUntil(this.unSubscribe) added to this page cancels on leave instead of silently leaking.
    this.unSubscribe.next();
    this.consentsUnSubscribe.next();
    // getTenantAndUserSubscription is deliberately NOT nexted here. Cancelling ionViewDidEnter's
    // tenant/user chain on leave looks right and is not: its completion handler owns the loading
    // spinner — an app-wide overlay — and hiding that from here either strands it, or steals the
    // one MenuComponent's native sign-out raises before navigating away, or fires
    // hideLoadingSpinnerDialog()'s unconditional 1s re-sweep into whatever the *next* page has
    // presented by then. It also runs checkParams(), which is what registers a card from a
    // /go?registrationCode=… deep link.
    //
    // So the chain is left to finish, and the work it does is made safe instead: pageIsActive
    // above stops setListeners() subscribing on a page that is gone.
  }

  ionViewDidEnter(): void {
    this.pageIsActive = true;
    this.pageTitleService.setTitle('CardsCodesAndConsents');
    this.dialogService.showLoadingSpinnerDialog('ionViewDidEnter->refeshCurrentTenant()').subscribe(() => {
      this.tenantService.refreshStoredTenant()
      .pipe(takeUntil(this.getTenantAndUserSubscription))
      .subscribe(() => {
        this.userService.refreshCurrentUser()
        .pipe(takeUntil(this.getTenantAndUserSubscription))
        .subscribe(() => {
          this.dialogService.hideLoadingSpinnerDialog('ionViewDidEnter->refeshCurrentTenant()');
          // complete() here, unlike the two subjects nexted in ionViewWillLeave() — an intentional
          // exception to the rule stated there, and the same shape as the six sibling pages. This
          // one is only ever nexted on this line, so closing it gives up no cancellation, and it
          // stops a later visit's success nexting a notifier a still-in-flight chain is listening
          // to. It does not help two chains that overlap before any success: the first to land
          // still cancels the other. Deleting this line widens that from once to every overlap.
          this.getTenantAndUserSubscription.next();
          this.getTenantAndUserSubscription.complete();
          this.eventService.startTokenExpireCountDown();
          this.setListeners();
          this.initPage();
        });
      }, (error) => {
        this.dialogService.hideLoadingSpinnerDialog('ERROR - ionViewDidEnter->refeshCurrentTenant()');
        this.errorService.handleHttpClientResponseError(error, 'GET', 'handleUserAuthentication')
        .subscribe(result => {
          if (result === 'unauthenticated') {
            this.logger.info('User unauthenticated');
          }
        });
      });
    });
  }

  private setListeners() {
    // Both guards exist because this can run on a page the user has already left: the tenant/user
    // chain in ionViewDidEnter() is deliberately not cancelled on leave, so it can resolve
    // afterwards and call this.
    //
    // Skip entirely when the page is gone — otherwise this subscribes a reload listener and a
    // resume handler nobody will ever tear down, since ionViewWillLeave() has already run. Only the
    // next visit would reclaim them, so a user who abandons /go once and never returns would keep
    // firing a consents fetch on every foreground for the rest of the session.
    if (!this.pageIsActive) {
      this.logger.info('setListeners() - page already left, not subscribing');
      return;
    }

    // Tear down before reassigning, for the case the guard cannot cover: a second visit whose
    // listeners are set while the first visit's pair is somehow still live.
    this.reloadPageEventSubscription?.unsubscribe();
    this.resumeSubscription?.unsubscribe();

    this.reloadPageEventSubscription = this.broadcaster.on('reload_page').subscribe(() => {
      this.logger.info('Token expire countdown - reload_page');
       location.reload();
    });
    // Refreshes the list when the app is foregrounded *while this page is showing* — a consent
    // granted on another device, or an admin change.
    //
    // Native only. Ionic's Platform.resume proxies the 'resume' DOM event on document
    // (@ionic/angular ionic-angular-common.mjs, proxyEvent(this.resume, doc, 'resume', zone)),
    // which Cordova fires and browsers do not, so on web and PWA this never emits. Web users get
    // the list refreshed on page re-entry only, which is the same path native falls back to.
    //
    // It deliberately does **not** cover the Authorize round trip on either platform:
    // navigateForward() leaves this page first, so ionViewWillLeave() has already dropped this
    // subscription, and the user returns through ionViewDidEnter() → initPage() →
    // loadDelegatedConsents() anyway.
    this.resumeSubscription = this.platform.resume.subscribe(() => this.loadDelegatedConsents());
  }

  private initPage () {
    this.logger.info('initPage()');
    this.pinCodeConflict = false;
    this.handleUserPermissions();
    this.loadDelegatedConsents();

    // Execute all resource loading in parallel and wait for completion
    forkJoin({
      pinCode: this.getPinCode(),
      idCode: this.getIdCode(),
      cards: this.getCards()
    }).pipe(
      finalize(() => {
        // Always run checkParams when forkJoin completes (success or error)
        this.checkParams();
      })
    ).subscribe({
      next: () => {
        this.logger.info('All user resources loaded successfully');
      },
      error: (error) => {
        // Handle errors from any of the resource loading methods
        // Only non-404 errors reach here (404s are handled in each method)
        // this.logger.error('Error loading user resources:', error);

        // Deliberately NOT piped through takeUntil(this.unSubscribe). On a 401/403 this returns a
        // cold sign-out chain — signOutUser('session-ended') followed by triggerUnauthenticated()
        // in a tap, with the same call in its catchError. Unsubscribing is neither next nor error,
        // so cancelling it runs neither: the revoke is abandoned part-done, user/tenant/Pusher
        // state stays populated, and nothing ever redirects to sign-in. That path shows the user
        // nothing, so leaving the page while it runs is entirely plausible. The other branches do
        // their work eagerly and return of(...), so there is nothing there to cancel either.
        this.errorService.handleHttpClientResponseError(error, 'GET', 'initPage - user resources')
          .subscribe();
      }
    });
  }

  ///////////////////////////////////////////
  ////////// DELEGATED CONSENTS /////////////
  ///////////////////////////////////////////

  // Loaded outside initPage()'s forkJoin so the section can be absent without affecting
  // the PIN code, ID code and card resources.
  private loadDelegatedConsents(): void {
    const methodName = 'loadDelegatedConsents()';
    const tenantSelfLink = this.tenant()?.links?.self;
    if (!tenantSelfLink) {
      // Same case as the service's own no-valid-link return, and answered the same way: no request
      // was made, so writing [] would claim the backend said there is nothing. On a resume refetch
      // that is the erasure the null contract exists to prevent.
      this.logger.info(methodName + ' no tenant self link, keeping the current list');
      return;
    }
    // Cancel any fetch still in flight. Two can overlap — initPage() on re-entry racing a
    // platform.resume, or two foregrounds in quick succession — and without this whichever
    // response lands last writes the signal, which is not necessarily the newest request.
    this.consentsUnSubscribe.next();
    this.delegatedConsentsService.getDelegatedConsents(tenantSelfLink)
      .pipe(takeUntil(this.consentsUnSubscribe))
      .subscribe((consents: IDelegatedConsent[] | null) => {
        // null is "could not ask", not "nothing to authorize". This also runs on platform.resume,
        // where writing [] would erase a list that was rendering fine — foreground the app a second
        // before the network is back and the section would vanish until the page was re-entered.
        // Keeping the last known good costs little: a granted consent flips consentRequired to
        // false, it does not disappear from the list.
        if (consents === null) {
          this.logger.info(methodName + ' unavailable, keeping the current list');
          return;
        }
        this.logger.info(methodName + ' consents: ' + consents.length);
        this.delegatedConsents.set(consents);
      });
  }

  public showDelegatedConsents(): boolean {
    return this.delegatedConsents().length > 0;
  }

  // Brand names, so not translated. A directory can appear once per kind, which is the only
  // thing distinguishing the two rows.
  public consentKindLabel(consent: IDelegatedConsent): string {
    switch (consent.kind) {
      case 'onedrive':
        return 'OneDrive';
      case 'sharepoint':
        return 'SharePoint';
      default:
        return consent.kind;
    }
  }

  // Navigates internally rather than opening consentUrl, which points back at this app.
  public authorizeDelegatedConsent(consent: IDelegatedConsent): void {
    const methodName = 'authorizeDelegatedConsent()';
    if (!consent.consentUrl) {
      this.logger.error(methodName + ' No consent link on this entry');
      return;
    }
    try {
      const consentUrl = new URL(consent.consentUrl);
      // Every parameter must be present before navigating. Angular drops null query params, so a
      // link missing one would reach /activate-delegated, fail its validation there and take the
      // silent /print-jobs fallback — the user taps Grant access and lands on the print jobs list
      // with nothing naming what was wrong. Named here instead, while we still know.
      const queryParams: { [name: string]: string } = {};
      for (const name of ['kind', 'providerId', 'directory', 'redirect_uri']) {
        const value = consentUrl.searchParams.get(name);
        if (!value) {
          this.logger.error(methodName + ' Consent link is missing ' + name);
          return;
        }
        queryParams[name] = value;
      }
      // Values are validated properly by ActivateDelegatedPage; this only checks they are there.
      this.navController.navigateForward(['/activate-delegated'], { queryParams });
    } catch (error) {
      this.logger.error(methodName + ' Malformed consent URL');
    }
  }

  private handleUserPermissions() {
    this.logger.info('handleUserPermissions()');
    const userPermissions = this.user().embedded.permissions.permissions;
    const userPermissionsSet = new Set(userPermissions);

    if (userPermissionsSet.has('RESOURCE_PINCODE_CREATE_OWN')) {
      this.userManagePinCode = true;
    }
    if (userPermissionsSet.has('RESOURCE_ID_CODES_CREATE_OWN')) {
      this.userManageIdCode = true;
    }
    if (userPermissionsSet.has('RESOURCE_CARD_CREATE_OWN')) {
      this.userManageCard = true;
    }
  }

  ///////////////////////////////////////////
  ///////////////////////////////////////////
  /////// CHECK PARAMS
  ///////////////////////////////////////////
  private checkParams () {
    this.logger.info('checkParams()');
    const params = location.search;
    if (params.includes('registrationCode') || params.includes('registrationCode=FORGOT_CODE')) {
      if (!this.userManageCard) { // does the user have permission to register a card?
        this.logger.info('checkParams() - showRegisterCardDeniedToast');
        const permissionMessage = this.translateMessageWithParameter('ACCESS_DENIED');
        this.errorToast(permissionMessage);
        return;
      } else if (params.includes('registrationCode') && !params.includes('registrationCode=FORGOT_CODE')) {
        const splitParams = params.split('&');
        for (let i = 0; i < splitParams.length; i++) {
          if (splitParams[i].includes('registrationCode')) {
            this.cardCode = splitParams[i].split('=')[1];
            if (this.cardCode) {
              this.registerCardWebAppScan();
            }
          }
        }
      } else {
        if (params.includes('registrationCode=FORGOT_CODE')) {
          this.enterCardCode = true;
        }
      }
    }
  }

  ///////////////////////////////////////////
  ///////////////////////////////////////////
  /////// CARD STUFF
  ///////////////////////////////////////////
  public getCards (): Observable<any> {
    this.logger.info('getCards()');

    return this.user().api.getCards(this.user()).pipe(
      tap({
        next: (cards: any) => {
          if (cards && cards._embedded) {
            this.cards = cards._embedded['px:cards'];
            this.logger.info('Cards loaded successfully');
          } else {
            this.cards = [];
          }
        },
        error: () => {
          this.cards = [];
        }
      }),
      catchError((error) => {
        // For 404 errors (expected), return null to keep forkJoin happy
        if (error.status === 404) {
          this.logger.info('No cards found (404 - expected)');
          return of(null);
        }

        // For all other errors, propagate to forkJoin for centralized handling
        throw error;
      })
    );
  }

  public deleteCard(card: any) {
    this.logger.info('deleteCard()');

    this.user().api.deleteCard(card).pipe(
      tap(() => {
        const message = this.translateMessageWithParameter('CardDeleted');
        this.successToast(message);
        this.getCards().subscribe();
      }),
      catchError((error) => {
        let message = this.translateMessageWithParameter('ERROR');
        if (error.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        }
        this.errorToast(message);
        return of(null);
      })
    ).subscribe();
  }

  public registerCardDialog () {
    this.logger.info('registerCardDialog()');
    this.enterPinCode = false;
    this.cardCode = '';
    this.invalidCardCode = false;
    this.enterCardCode = !this.enterCardCode;
  }

  public checkCardCode (event) {
    this.logger.info('checkCardCode()');

    this.cardCode = event;
    const numRegex = /^[0-9]\d*$/;
    if (!numRegex.test(this.cardCode)) {
      this.invalidCardCode = true;
    } else {
      this.invalidCardCode = false;
    }

    if (this.cardCode.length < 6) {
      this.incompleteCardCode = true;
    }

    if (this.cardCode.length === 0) {
      this.invalidCardCode = false;
    }

    if (numRegex.test(this.cardCode) && this.cardCode.length === 6) {
      this.invalidCardCode = false;
      this.incompleteCardCode = false;
    }
  }

  public registerCardWebAppScan() {
    this.logger.info('registerCardWebAppScan()');
    this.enterCardCode = false;

    this.user().api.registerCard(this.user(), this.cardCode).pipe(
      tap((response) => {
        const message = this.translateMessageWithParameter('CardRegistered');
        this.successToast(message);
        this.getCards().subscribe();
        history.pushState({}, null, '/');
      }),
      catchError((error) => {
        let message = this.translateMessageWithParameter('ERROR');
        if (error.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        } else if (error.status === 409) {
          this.logger.info('Card already registered (409 - expected)');
        }
        this.errorToast(message);
        history.pushState({}, null, '/');
        return of(null);
      })
    ).subscribe();
  }

  public registerCard() {
    this.logger.info('registerCard()');
    this.enterCardCode = false;

    this.user().api.registerCard(this.user(), this.cardCode).pipe(
      tap(() => {
        const message = this.translateMessageWithParameter('CardRegistered');
        this.successToast(message);
        setTimeout(() => {
          this.getCards().subscribe();
        }, 1000);
      }),
      catchError((error) => {
        let message = this.translateMessageWithParameter('ERROR');
        if (error.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        } else if (error.status === 404) {
          message = this.translateMessageWithParameter('InvalidCardCode');
        }
        this.errorToast(message);
        return of(null);
      })
    ).subscribe();
  }

  ///////////////////////////////////////////
  ///////////////////////////////////////////
  /////// ID CODE STUFF
  ///////////////////////////////////////////
  public getIdCode(): Observable<any> {
    this.logger.info('getIdCode()');
    this.gettingIdCode = true;

    return this.user().api.getIdCode(this.user()).pipe(
      tap({
        next: (idCode) => {
          this.gettingIdCode = false;
          if (idCode) {
            this.idCodeResource = idCode;
            this.logger.info('ID code loaded successfully');
          }
        },
        error: () => {
          this.gettingIdCode = false;
        }
      }),
      catchError((error) => {
        // For 404 errors (expected), return null to keep forkJoin happy
        if (error.status === 404) {
          this.logger.info('No ID code found (404 - expected)');
          return of(null);
        }

        // For all other errors, propagate to forkJoin for centralized handling
        throw error;
      })
    );
  }

  public setIdCode() {
    this.logger.info('setIdCode()');
    this.gettingIdCode = true;
    this.user().api.setIdCode(this.tenant()).pipe(
      tap((idCode) => {
        this.gettingIdCode = false;
        this.idCodeResource = idCode;
        const message = this.translateMessageWithParameter('ChangesSaved');
        this.successToast(message);
      }),
      catchError((error) => {
        this.gettingIdCode = false;
        let message = this.translateMessageWithParameter('ERROR');
        if (error.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        }
        this.errorToast(message);
        return of(null);
      })
    ).subscribe();
  }

  public resetIdCode() {
    this.logger.info('resetIdCode()');
    this.gettingIdCode = true;
    this.user().api.deleteIdCode(this.idCodeResource).pipe(
      tap((response) => {
        this.gettingIdCode = false;
        this.setIdCode();
      }),
      catchError((error) => {
        this.gettingIdCode = false;
        let message = this.translateMessageWithParameter('ERROR');
        if (error.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        }
        this.errorToast(message);
        return of(null);
      })
    ).subscribe();
  }

  public deleteIdCode() {
    this.logger.info('deleteIdCode()');
    this.gettingIdCode = true;
    this.user().api.deleteIdCode(this.idCodeResource).pipe(
      tap((response) => {
        this.gettingIdCode = false;
        this.idCodeResource = null;
        const message = this.translateMessageWithParameter('ChangesSaved');
        this.successToast(message);
      }),
      catchError((error) => {
        this.gettingIdCode = false;
        let message = this.translateMessageWithParameter('ERROR');
        if (error.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        }
        this.errorToast(message);
        return of(null);
      })
    ).subscribe();
  }

  ///////////////////////////////////////////
  ///////////////////////////////////////////
  /////// PIN CODE STUFF
  ///////////////////////////////////////////
  public getPinCode (): Observable<any> {
    this.logger.info('getPinCode()');
    this.gettingPinCode = true;

    return this.user().api.getPinCode(this.user()).pipe(
      tap({
        next: (response) => {
          this.gettingPinCode = false;
          if (response) {
            this.pinCodeResource = response;
            this.logger.info('Pin code loaded successfully');
          }
        },
        error: () => {
          this.gettingPinCode = false;
        }
      }),
      catchError((error) => {
        // For 404 errors (expected), return null to keep forkJoin happy
        if (error.status === 404) {
          this.logger.info('No pin code found (404 - expected)');
          return of(null);
        }

        // For all other errors, propagate to forkJoin for centralized handling
        throw error;
      })
    );
  }

  public toggleEnterPinCode () {
    this.logger.info('toggleEnterPinCode()');

    this.enterPinCode = !this.enterPinCode;
    this.newPinCode = '';
    this.invalidPinCode = false;
    this.enterCardCode = false;
  }

  public checkPinCode (event) {
    this.logger.info('checkPinCode()');
    this.newPinCode = event;
    const numRegex = /^[0-9]\d*$/;
    if (!numRegex.test(this.newPinCode)) {
      this.invalidPinCode = true;
    } else {
      this.invalidPinCode = false;
    }

    if (this.newPinCode.length < 4) {
      this.incompletePinCode = true;
    }

    if (this.newPinCode.length === 0) {
      this.invalidPinCode = false;
      this.incompletePinCode = true;
    }

    if (numRegex.test(this.newPinCode) && this.newPinCode.length === 4) {
      this.invalidPinCode = false;
      this.incompletePinCode = false;
    }
  }

  public setPinCode() {
    this.logger.info('setPinCode()');
    this.enterPinCode = false;
    this.pinCodeConflict = false;
    this.gettingPinCode = true;

    this.user().api.setPinCode(this.user(), this.newPinCode).pipe(
      tap((response) => {
        this.gettingPinCode = false;
        this.pinCodeResource = response;
        const message = this.translateMessageWithParameter('ChangesSaved');
        this.successToast(message);
      }),
      catchError((error) => {
        this.gettingPinCode = false;
        if (error.status === 409) {
          this.pinCodeConflict = true;
        } else {
          let message = this.translateMessageWithParameter('ERROR');
          if (error.status === 403) {
            message = this.translateMessageWithParameter('ACCESS_DENIED');
          }
          this.errorToast(message);
        }
        return of(null);
      })
    ).subscribe();
  }

  public deletePinCode() {
    this.logger.info('deletePinCode()');
    this.dialogService.showLoadingSpinnerDialog('deletePinCode()').subscribe(() => {
      this.user().api.deletePinCode(this.pinCodeResource).subscribe((response) => {
        this.dialogService.hideLoadingSpinnerDialog('deletePinCode()');
        this.pinCodeResource = null;
        const message = this.translateMessageWithParameter('ChangesSaved');
        this.successToast(message);
      }, (httpErrorResponse) => {
        this.dialogService.hideLoadingSpinnerDialog('ERROR - deletePinCode()');
        let message = this.translateMessageWithParameter('ERROR');
        if (httpErrorResponse.status === 403) {
          message = this.translateMessageWithParameter('ACCESS_DENIED');
        }
        this.errorToast(message);
      });
    });
  }

  public showPinCodeTitle(): boolean {
    return this.pinCodeResource ||
           !this.idCodeResource;
  }

  public showPinCodeRequiredText(): boolean {
    return this.idCodeResource &&
           this.idCodeResource.pincodeRequired &&
           !this.pinCodeResource;
  }

  public showResetPinCodeButton(): boolean {
    return !this.gettingPinCode &&
           this.pinCodeResource &&
           !this.enterPinCode &&
           this.userManagePinCode;
  }

  public showCreatePinCodeButton(): boolean {
    return !this.gettingPinCode &&
           !this.pinCodeResource &&
           !this.enterPinCode &&
           this.userManagePinCode;
  }

  public showPinCodeDisabled(): boolean {
    return this.pinCodeResource &&
           this.pinCodeResource.disabled &&
           !this.pinCodeConflict;
  }

  public showPinCodeDots(): boolean {
    return !this.gettingPinCode &&
           this.pinCodeResource &&
           !this.enterPinCode &&
           !this.pinCodeResource.disabled &&
           !this.pinCodeConflict;
  }

  public showDeletePinCodeIcon(): boolean {
    return !this.gettingPinCode &&
           this.pinCodeResource &&
           !this.enterPinCode &&
           !this.pinCodeConflict &&
           this.userManagePinCode;
  }

  public showNoPinCodeDash(): boolean {
    return !this.gettingPinCode &&
           !this.pinCodeResource &&
           !this.enterPinCode;
  }

  public showResetIdCodeButton(): boolean {
    return this.idCodeResource &&
           this.userManageIdCode;
  }

  public showCreateIdCodeButton(): boolean {
    return !this.idCodeResource &&
           this.userManageIdCode;
  }

  public showNoIdCodeDash(): boolean {
    return !this.gettingIdCode &&
           !this.idCodeResource;
  }

  public showDeleteIdCodeIcon(): boolean {
    return this.idCodeResource &&
           this.userManageIdCode;
  }

  public showRegisterCardDialogButton(): boolean {
    return !this.enterCardCode &&
           this.userManageCard;
  }

  public showRegisterCardForm(): boolean {
    return this.enterCardCode &&
           this.userManageCard;
  }

  public showDeleteCardIcon(): boolean {
    return this.userManageCard;
  }



  ///////////////////////////////////////////
  ///////////////////////////////////////////
  /////// TOAST
  ///////////////////////////////////////////
  private translateMessageWithParameter(text: string, args?: string[]): string {
    if (text) {
      // No next()/complete() on unSubscribe here. It used to tear the page's subject down from
      // inside a translation callback, which completed it on the first toast and left every other
      // takeUntil(this.unSubscribe) on this page unable to cancel anything. get() emits once and
      // completes on its own, so no manual teardown is needed; the takeUntil below is kept only so
      // the chain is not left running if a language load happens to be in flight.
      //
      // Note this method relies on get() emitting *synchronously* — it assigns to `text` inside the
      // subscribe and returns it below. True whenever translations are loaded; while a load is in
      // flight the raw key is returned instead and the toast shows it. Pre-existing.
      this.translateService.get(text)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((value: string) => {
        text = value;
      });
      return text;
    }
  }

  private successToast (message: string) {
    const toast = this.toastController.create({
      message: message,
      color: 'success',
      duration: 3000,
      position: 'top'
    }).then(toast => toast.present());
  }

  private errorToast (message: string) {
    const toast = this.toastController.create({
      message: message,
      color: 'danger',
      duration: 3000,
      position: 'top'
    }).then(toast => toast.present());
  }

  ///////////////////////////////////////////
  ///////////////////////////////////////////
  /////// QR SCANNER STUFF
  ///////////////////////////////////////////

  // button to open camera and scan qr code
  public openQRScanner(): void { // VIEW
    const methodName = 'QR openQRScanner() ';
    this.logger.info(methodName);

    this.registerCardScannerQR.prepare().then((status: QRScannerStatus) => {
      if (status.authorized) {
        this.toggleQRScanningState(true);
        this.scanQRCode();
      } else if (status.denied) {
        // camera permission was permanently denied
        // you must use QRScanner.openSettings() method to guide the user to the settings page
        // then they can grant the permission from there
        this.logger.info(methodName + 'status.denied, the user needs to authorize the app to use the camera from the settings, in order to scan QR codes');
        this.dialogService.showAlertDialog('You need to authorize the app to use the camera from the settings, in order to scan QR codes').subscribe();
      } else {
        this.logger.info(methodName + 'status.denied, the user needs to authorize the app to use the camera from the settings, in order to scan QR codes');
        this.dialogService.showAlertDialog('You need to authorize the app to use the camera from the settings, in order to scan QR codes').subscribe();
        // permission was denied, but not permanently. You can ask for permission again at a later time.
      }
    })
    .catch((e: any) => {
      this.logger.info(methodName + 'qrScanner.prepare() ERROR: ', e);
    });
  }

  public toggleQRScanningState(on: boolean): void {
    this.logger.info('toggleQRScanningState()');

    if (on) {
      window.document.querySelector('ion-app').classList.add('transparentBody');
      window.document.querySelector('ion-content').classList.add('transparentBody');
      this.registerCardScannerQR.show();
    } else {
      window.document.querySelector('ion-app').classList.remove('transparentBody');
      window.document.querySelector('ion-content').classList.remove('transparentBody');
      this.registerCardScannerQR.hide();
      this.registerCardScannerQR.destroy();
    }
    this.showTransparentBody = on;
    this.ref.detectChanges();
  }

  public scanQRCode(): void { // openQRScanner()
    this.logger.info('scanQRCode()');

    // take(1) states the one-shot contract locally. Belt-and-braces rather than a live fix: both
    // native implementations null the scan callback after a single result, so a second emission is
    // not something they can produce. What it replaces is next()/complete() on the page's
    // unSubscribe, called from inside this handler, which also silently disabled every other
    // takeUntil(this.unSubscribe) on the page from that point on.
    this.registerCardScannerQR.scan()
    .pipe(take(1), takeUntil(this.unSubscribe))
    .subscribe((qrUrl: string) => {
      this.logger.info('scanQRCode() qrScanner.scan()');
      this.vibration.vibrate(1000);
      this.cardCode = qrUrl.split('=')[1];
      this.cancelQRScanner();
      this.registerCard();
    });
  }

  public cancelQRScanner(): void {
    this.logger.info('cancelQRScanner()');
    setTimeout(() => {
      this.invalidQRCode = false;
      this.toggleQRScanningState(false);
    }, 300);
  }
}
